import { useState, ChangeEvent, FormEvent } from 'react';

type ValidationResult = true | Record<string, string | string[]>;
type Validations<T> = {
    [K in keyof T]?: (value: T[K], allValues: T) => ValidationResult;
};

interface UseFormReturn<T> {
    values: T;
    reset: () => void;
    errors: Record<string, string | string[]>;
    customUpdate: (update: Partial<T>) => void;
    changeHandler: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    submitHandler: (event: FormEvent) => void;
    setCustomError: (name: string, error: string | string[]) => void;
}

export const useForm = <T,>(
    initialState: T,
    initialErrors: Record<string, string | string[]> = {},
    initialValidations: Validations<T> = {},
    onSubmit: (values: T) => void = () => { /* no-op */ }
): UseFormReturn<T> => {
    const [values, setValues] = useState<T>(initialState);
    const [errors, setErrors] = useState<Record<string, string | string[]>>(initialErrors);
    const [validations] = useState<Validations<T>>(initialValidations);

    const reset = () => {
        setValues(initialState);
        setErrors(initialErrors);
    };

    const customUpdate = (update: Partial<T>) => setValues({ ...values, ...update });

    const changeHandler = ({ target: { name, value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const newState = { ...values, [name]: value };
        setValues(newState);
    };

    const submitHandler = (event: FormEvent) => {
        event.preventDefault();
        let isFormValid = true;
        let errors: Record<string, string | string[]> = {};
        for (const key in validations) {
            const typedKey = key as keyof T;
            const validationFn = validations[typedKey];
            if (!validationFn) continue;

            const isValid = validationFn(values[typedKey], values);
            isFormValid = isFormValid && isValid === true;
            if (isValid !== true) {
                errors = { ...errors, ...isValid };
            }
        }
        setErrors(errors);

        if (isFormValid === true) {
            onSubmit(values);
        }
    };

    const setCustomError = (name: string, error: string | string[]) => setErrors({ ...errors, [name]: error });

    return { values, reset, errors, customUpdate, changeHandler, submitHandler, setCustomError };
};

export const validators = {
    hasAmount: (value: string): boolean => /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(value),
    hasPercentage: (value: string): boolean => /(^100(\.0{1,2})?$)|(^([1-9]([0-9])?|0)(\.[0-9]{1,2})?$)/.test(value),
    hasNumber: (value: string): boolean => /^(\s*|\d+)$/.test(value),
    hasValue: (value: string): boolean => value.trim().length > 0,
    isValidEmail: (value: string): boolean => (
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)
    ),
    validatePassword: (value: string): true | { password: string[] } => {
        const lowerCaseRegex = /[a-z]/g;
        const upperCaseRegex = /[A-Z]/g;
        const numbersRegex = /[0-9]/g;
        const specialCharacterRegex = /[^a-zA-Z0-9]/g;
        const passwordRules = {
            minLength: {
                isValid: (value: string) => !!value && value.length >= 9,
                error: `Password must be at least 9 characters long`
            },
            maxLength: {
                isValid: (value: string) => !value || value.length <= 100,
                error: `Password must be 100 characters or less`
            },
            lowerCase: {
                isValid: (value: string) => !!value && value.match(lowerCaseRegex) !== null,
                error: `Password needs at least 1 lowercase letter`
            },
            upperCase: {
                isValid: (value: string) => !!value && value.match(upperCaseRegex) !== null,
                error: `Password needs at least 1 uppercase letter`
            },
            number: {
                isValid: (value: string) => !!value && value.match(numbersRegex) !== null,
                error: `Password needs at least 1 number`
            },
            special: {
                isValid: (value: string) => !!value && value.match(specialCharacterRegex) !== null,
                error: `Password needs at least 1 special character (like @, $, or %)`
            }
        };
        let isValid = true;
        const errors: string[] = [];
        Object.keys(passwordRules).forEach((r) => {
            const key = r as keyof typeof passwordRules;
            const isRuleValid = passwordRules[key].isValid(value);
            isValid = isValid && isRuleValid;
            if (!isRuleValid) errors.push(passwordRules[key].error);
        });
        return isValid || { password: errors };
    }
};
